import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
} from '@angular/core';
import {
  HighlightIndices,
  Option,
  TreeNode,
  TreeOption,
} from '@rivethealth/data/options';
import Fuse, { FuseResultMatch } from 'fuse.js';
import { isEmpty, min, sortBy } from 'lodash';
import { ObservableInput } from 'observable-input';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  scan,
  startWith,
  withLatestFrom,
} from 'rxjs/operators';

type State = {
  expand: { [id: string]: boolean };
  matches: { [id: string]: FuseResultMatch[] };
  scores: { [id: string]: number };
};

/**
 * A hierarchical multi-select with text search.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tree-multi-select',
  styleUrls: ['./tree-multi-select.component.scss'],
  templateUrl: './tree-multi-select.component.html',
})
export class TreeMultiSelectComponent {
  readonly filter = new BehaviorSubject<string>('');
  readonly filterEmpty = new BehaviorSubject<boolean>(false);

  readonly expand = new BehaviorSubject<[string | null, boolean]>([
    null,
    false,
  ]);
  readonly select = new BehaviorSubject<[string | null, boolean]>([
    null,
    false,
  ]);

  @Input()
  private tokenize: boolean = false;

  @Input()
  displayId: boolean;

  @ObservableInput()
  @Input('autoFocus')
  private readonly _autoFocus!: Observable<boolean>;
  readonly defaultedAutoFocus = this._autoFocus.pipe(startWith(false));

  @ObservableInput()
  @Input('disabled')
  private readonly _disabled!: Observable<boolean>;
  readonly defaultedDisabled = this._disabled.pipe(startWith(false));

  @ObservableInput()
  @Input('exact')
  private readonly _exact!: Observable<boolean>;
  readonly defaultedExact = this._exact.pipe(startWith(false));

  @ObservableInput()
  @Input('filterable')
  private readonly _filterable!: Observable<boolean>;
  readonly defaultedFilterable: Observable<boolean> = this._filterable.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('filterPositionBottom')
  private readonly _filterPositionBottom!: Observable<boolean>;
  readonly defaultedFilterPositionBottom = this._filterPositionBottom.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('options')
  private readonly _options!: Observable<TreeOption[]>;
  readonly defaultedOptions: Observable<TreeOption[]> = this._options.pipe(
    startWith([]),
    map(options => options || []),
  );

  @ObservableInput()
  @Input('selectedOptions')
  private _selectedOptions!: Observable<string[]>;
  readonly defaultedSelectedOptions = this._selectedOptions.pipe(startWith([]));

  @Input()
  disabledOptions: string[] = [];

  isNodeDisabled(node: TreeNode): boolean {
    if (!node.children || node.children.length == 0) {
      return this.disabledOptions.includes(node.id);
    } else {
      return node.children.every(node => this.isNodeDisabled(node));
    }
  }

  private readonly flatOptions: Observable<TreeOption[]> =
    this.defaultedOptions.pipe(
      map(options => {
        const allOptions: TreeOption[] = [];
        (function visit(options: TreeOption[]) {
          for (const option of options) {
            allOptions.push(option);
            visit(option.children);
          }
        })(options);
        return allOptions;
      }),
    );

  private readonly fuzzySearch: Observable<Fuse<Option> | null> = combineLatest(
    [this.flatOptions, this.defaultedExact, this.defaultedFilterable],
  ).pipe(
    map(([options, exact, filterable]) => {
      return filterable
        ? new Fuse<Option>(options, {
            keys: ['title', 'subtitle'],
            includeMatches: true,
            includeScore: true,
            ignoreLocation: this.tokenize,
            threshold: exact ? 0.0 : 0.6,
          })
        : null;
    }),
  );

  private readonly matchIndices: Observable<
    [
      {
        [id: string]: FuseResultMatch[];
      },
      { [id: string]: number },
    ]
  > = combineLatest([
    this.fuzzySearch,
    this.filter.pipe(debounceTime(100)),
    this.flatOptions,
  ]).pipe(
    map(([fuzzySearch, filter, options]) => {
      const matchedNodes: { [id: string]: FuseResultMatch[] } = {};
      const scores: { [id: string]: number } = {};
      if (filter.length > 0) {
        const matches = fuzzySearch!.search(filter);
        if (matches.length) {
          for (const match of matches) {
            matchedNodes[match.item.id] = [...(match.matches || [])];
            scores[match.item.id] = match.score || 1;
          }
          this.filterEmpty.next(false);
        } else {
          this.filterEmpty.next(true);
        }
      } else {
        this.filterEmpty.next(false);
      }
      return [matchedNodes, scores];
    }),
  );

  private readonly state: Observable<State> = merge(
    this.matchIndices.pipe(map(value => ({ type: 'matches', value }))),
    this.defaultedOptions.pipe(
      map(() => {
        return { type: 'options' };
      }),
    ),
    this.expand.pipe(map(value => ({ type: 'expand', value }))),
  ).pipe(
    scan<any, State>(
      (state, event) => {
        let id, expanded;
        if (!event) return state;
        switch (event.type) {
          case 'expand':
            [id, expanded] = event.value;
            state.expand[id] = expanded;
            break;
          case 'options':
            //state.expand = {}; //Let the user manage the expanded state not rivet
            break;
          case 'matches':
            state.matches = event.value[0];
            state.scores = event.value[1];
            break;
        }
        return state;
      },
      { expand: {}, matches: {}, scores: {} },
    ),
  );

  readonly nodes: Observable<TreeNode[]> = combineLatest([
    this.defaultedOptions,
    this.state,
    this.defaultedSelectedOptions,
  ]).pipe(
    map(([options, state, selectedOptions]) => {
      return buildNodes(options, state, selectedOptions, this.disabledOptions);
    }),
  );

  public readonly nodesLimited: Observable<TreeNode[]> = this.nodes.pipe(
    withLatestFrom(this.defaultedFilterable, this.filter),
    map(([nodes, filterable, filter]) => {
      return filterable && !!filter ? limitVisibleNodes(nodes, 100) : nodes;
    }),
  );

  public readonly notShownCount: Observable<number> = combineLatest([
    this.nodes,
    this.nodesLimited,
  ]).pipe(
    map(([nodes, nodesLimited]) =>
      Math.max(0, nodeCount(nodes) - nodeCount(nodesLimited)),
    ),
  );

  @Output()
  readonly selectedOptionsChange: Observable<string[]> = this.select.pipe(
    filter(select => {
      const [id] = select;
      return id !== null;
    }),
    withLatestFrom(this.nodes),
    map(([select, nodes]) => {
      const [id, selected] = select;
      updateSelection(nodes, id!, selected);
      return nodesToSelectedIds(nodes);
    }),
  );

  trackBy(index: number, node: TreeNode) {
    return node.id;
  }
}

function nodeCount(nodes: TreeNode[]): number {
  return (function count(nodes: TreeNode[]): number {
    return nodes
      ? nodes.reduce(
          (c, node) =>
            c + (node.children.length ? 1 + count(node.children) : 1),
          0,
        )
      : 0;
  })(nodes);
}

type LimitNodesState = {
  currentCount: number;
};

function limitVisibleNodes(nodes: TreeNode[], maxCount: number) {
  return (function filterChildren(
    nodes: TreeNode[],
    maxCount: number,
    state: LimitNodesState,
  ): TreeNode[] {
    const limitedNodes: TreeNode[] = [];
    for (const node of nodes) {
      if (state.currentCount < maxCount) {
        state.currentCount += 1;
        const nodeCopy = { ...node };
        limitedNodes.push(nodeCopy);
        if (
          nodeCopy.children.length &&
          (nodeCopy.expanded || nodeCopy.childrenFiltered)
        ) {
          nodeCopy.children = filterChildren(
            nodeCopy.children,
            maxCount,
            state,
          );
        }
      }
    }
    return limitedNodes;
  })(nodes, maxCount, { currentCount: 0 });
}

function buildNodes(
  options: TreeOption[],
  state: State,
  selectedOptions: string[],
  disabledOptions: string[],
): TreeNode[] {
  const sort = !isEmpty(state.scores);
  const nodes = (function build(
    options: TreeOption[],
    parent: TreeNode | null,
  ): TreeNode[] {
    const nodes = options
      ? options.map(option => {
          let titleHighlightIndices: HighlightIndices = [];
          let subtitleHighlightIndices: HighlightIndices = [];
          for (const fuseMatch of state.matches[option.id] || []) {
            switch (fuseMatch.key) {
              case 'title':
                titleHighlightIndices = fuseMatch.indices.concat();
                break;
              case 'subtitle':
                subtitleHighlightIndices = fuseMatch.indices.concat();
                break;
            }
          }
          const node: TreeNode = {
            id: option.id,
            title: option.title,
            subtitle: option.subtitle,
            count: option.count,
            subvertBox: option.subvertBox,
            subvertText: option.subvertText,
            link: option.link,
            parent,
            children: [],
            expanded: state.expand[option.id] || false,
            childrenFiltered: false, // filled in below
            selected: false, // filled in below
            titleHighlightIndices,
            subtitleHighlightIndices,
          };
          node.children = build(option.children, node);
          return node;
        })
      : [];
    return sort
      ? sortBy(nodes, node =>
          // better of this node's score and its descendants
          (function score(node: TreeNode): number {
            return min([
              // we have to check for null instead of falsy because 0 is a perfect match
              state.scores[node.id] !== null ? state.scores[node.id] : 1,
              ...node.children.map(score),
            ])!;
          })(node),
        )
      : nodes;
  })(options, null);
  fillInSelected(nodes, selectedOptions, disabledOptions);
  fillInChildrenFiltered(nodes);
  return nodes;
}

function fillInSelected(
  nodes: TreeNode[],
  selectedOptions: string[],
  disabledOptions: string[],
): void {
  const selectedIds = new Set(selectedOptions.map(o => o.toLowerCase()) || []);
  (function select(nodes: TreeNode[]): void {
    for (const node of nodes) {
      if (node.children.length) {
        select(node.children);
        node.selected = isInternalNodeSelected(node)
          ? !disabledOptions.includes(node.id)
          : isInternalNodeSelected(node);
      } else {
        node.selected =
          selectedIds.has(node.id.toLowerCase()) &&
          !disabledOptions.includes(node.id);
      }
    }
  })(nodes);
}

function fillInChildrenFiltered(nodes: TreeNode[]): void {
  (function fill(nodes: TreeNode[]): void {
    for (const node of nodes) {
      if (
        node.titleHighlightIndices.length ||
        node.subtitleHighlightIndices.length
      ) {
        for (
          let parent = node.parent;
          parent && !parent.childrenFiltered;
          parent = parent.parent
        ) {
          parent.childrenFiltered = true;
        }
      }
      if (node.children.length) {
        fill(node.children);
      }
    }
  })(nodes);
}

function nodesToSelectedIds(nodes: TreeNode[]): string[] {
  const selectedOptions: string[] = [];
  (function visit(nodes: TreeNode[]) {
    for (const node of nodes) {
      if (node.selected !== false) {
        if (node.children.length) {
          visit(node.children);
        } else if (node.selected) {
          selectedOptions.push(node.id);
        }
      }
    }
  })(nodes);
  return selectedOptions;
}

function updateSelection(
  nodes: TreeNode[],
  id: string,
  selected: boolean,
): void {
  (function visit(
    nodes: TreeNode[],
    id: string,
    selected: boolean,
    selectedDescendant: boolean,
  ) {
    for (const node of nodes) {
      const match = id === node.id || selectedDescendant;
      if (match) {
        node.selected = selected;
        if (id === node.id) {
          for (let parent = node.parent; parent; parent = parent.parent) {
            const selected = isInternalNodeSelected(parent);
            if (selected === parent.selected) {
              break;
            }
            parent.selected = selected;
          }
        }
      }
      if (node.children.length) {
        visit(node.children, id, selected, match);
      }
    }
  })(nodes, id, selected, false);
}

function isInternalNodeSelected(node: TreeNode): boolean | null {
  // NOTE: selected for internal nodes is a tri-state where
  // null means indeterminate (some children selected)
  return (
    node.children.every(node => node.selected) ||
    (node.children.some(node => node.selected !== false) && null)
  );
}
